import React, { createContext, useEffect, useMemo, useState } from 'react';
import { Loading } from '@components/index';
import {
  getSlug,
  isGoodfyndSitesPreview,
} from '@features/goodfynd-sites/utils/goodfynd-sites-util';
import { useOrganizationAppointments } from '@features/schedule/hooks/useOrganizationAppointments';
import {
  appointmentsToOperatingDayLocations,
  getCurrentOrNextEvent,
} from '@utils/schedule-util';
import { getPickupLocations } from '@utils/vendor-util';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { useStores } from 'stores';

import type {
  GoodfyndSiteContextValue,
  GoodfyndSiteDispatchContextValue,
  GoodfyndSiteProviderProps,
} from './types';
export const GoodfyndSiteContext = createContext<
  GoodfyndSiteContextValue | undefined
>(undefined);
export const GoodfyndSiteDispatchContext = createContext<
  GoodfyndSiteDispatchContextValue | undefined
>(undefined);

export default observer(function GoodfyndSiteProvider({
  children,
  organization,
}: GoodfyndSiteProviderProps) {
  const router = useRouter();

  const [currentPickupLocation, setCurrentPickupLocation] =
    useState<OperatingDayLocation>();

  const {
    data: { results: appointments = [] } = { results: [] },
    isFetching: isFetchingAppointments,
  } = useOrganizationAppointments(organization.organizationId);

  const {
    orderStore: { event, resetOrder },
  } = useStores();

  const pathScheduleId = useMemo(() => {
    return router.query.params?.includes('schedules')
      ? router.query.params[router.query.params.indexOf('schedules') + 1]
      : undefined;
  }, [router.query.params]);

  const overrideScheduleId = useMemo(() => {
    if (event?.scheduleId) {
      return event.scheduleId;
    }

    return pathScheduleId;
  }, [event?.scheduleId, pathScheduleId]);
  const locations = useMemo(() => {
    return appointmentsToOperatingDayLocations(appointments);
  }, [appointments]);
  const currentLocation = useMemo(
    () =>
      locations.find((location) => location.id === overrideScheduleId) ||
      getCurrentOrNextEvent(locations),
    [locations, overrideScheduleId]
  );

  const publicLocations = useMemo(
    () => locations?.filter((location) => !location?.isPrivate) || [],
    [locations]
  );

  useEffect(() => {
    if (
      event?.scheduleId &&
      pathScheduleId &&
      event.scheduleId !== pathScheduleId
    ) {
      resetOrder();
      setCurrentPickupLocation(undefined);
    }
  }, [event?.scheduleId, locations, pathScheduleId, resetOrder]);

  const orderingLocations = useMemo(
    () =>
      getPickupLocations(
        new Date(currentLocation?.end ?? new Date()),
        !!publicLocations[0],
        publicLocations
      ),
    [currentLocation?.end, publicLocations]
  );

  const value = useMemo<GoodfyndSiteContextValue>(
    () => ({
      currentLocation,
      currentPickupLocation,
      isFetchingAppointments,
      isPreview: isGoodfyndSitesPreview(),
      locations,
      orderingLocations,
      organization,
      publicLocations,
      slug: getSlug(),
    }),
    [
      currentLocation,
      currentPickupLocation,
      isFetchingAppointments,
      locations,
      orderingLocations,
      organization,
      publicLocations,
    ]
  );

  const theme = useMemo(() => {
    const organizationTheme = router.query.theme || organization.theme;
    return organizationTheme ? `theme-${organizationTheme}` : 'theme-black';
  }, [organization.theme, router.query.theme]);

  return (
    <GoodfyndSiteContext.Provider value={value}>
      <GoodfyndSiteDispatchContext.Provider
        value={{
          setCurrentPickupLocation,
        }}
      >
        <div className={theme}>
          {isFetchingAppointments ? <Loading type="line" /> : children}
        </div>
      </GoodfyndSiteDispatchContext.Provider>
    </GoodfyndSiteContext.Provider>
  );
});
